import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  PaymentSettlementBanner,
  computePaymentFormula,
  useShipmentPayments,
} from '#/entities/shipment'
import { MarketRateDialog, useSuggestedPrice } from '#/features/market-rate'
import type { MarketRateRow } from '#/features/market-rate'
import type { ShipmentDetail } from '#/shared/api/generated/shipment/models'
import { formatUsd } from '#/shared/lib/format'
import { cn } from '#/shared/lib/utils'
import { PriceCheckBadge } from '#/shared/ui/price-check-badge'
import { paymentToFormDefaults } from '#/widgets/payment-card'
import { SectionEditButton } from '#/widgets/shipment-card'

import { SidebarSection } from './sidebar-section'

function money(value: string | number | null | undefined): string {
  const num = typeof value === 'number' ? value : Number(value ?? 0)
  return formatUsd(Number.isFinite(num) ? num : 0, { showCents: true })
}

function MoneyBox({
  label,
  value,
  large,
}: {
  label: string
  value: string
  large?: boolean
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1 rounded-md bg-gray-100 p-1.5">
      <span className="truncate text-xs text-gray-500">{label}</span>
      <span
        className={cn(
          'truncate text-gray-900 tabular-nums',
          large ? 'text-2xl' : 'text-base',
        )}
      >
        {value}
      </span>
    </div>
  )
}

function MoneyGrid({ shipment }: { shipment: ShipmentDetail }) {
  const { t } = useTranslation()
  const formula = computePaymentFormula({
    totalTariff: shipment.tariff,
    deposit: shipment.deposit,
    carrierPay: shipment.carrier_pay,
  })
  const { totals } = useShipmentPayments(shipment.id)
  const defaults = paymentToFormDefaults(shipment)
  return (
    <div className="flex flex-col gap-2.5">
      <div className="grid grid-cols-3 gap-2.5">
        <MoneyBox
          label={t('payment.totalTariff')}
          value={money(shipment.tariff)}
          large
        />
        <MoneyBox
          label={t('payment.deposit')}
          value={money(shipment.deposit)}
          large
        />
        <MoneyBox
          label={t('payment.carrierFee')}
          value={money(shipment.carrier_pay)}
          large
        />
      </div>
      <div className="grid grid-cols-3 gap-2.5">
        <MoneyBox
          label={t('payment.dueOnDelivery')}
          value={money(formula.dueOnDelivery)}
        />
        <MoneyBox
          label={t('payment.balance')}
          value={money(formula.balanceAbsolute)}
        />
        <MoneyBox
          label={t('payment.paidToCarrier')}
          value={totals?.paidToCarrier ?? money(0)}
        />
      </div>
      <PaymentSettlementBanner formula={formula} values={defaults} />
    </div>
  )
}

export function LoadboardPaymentSummary({
  shipment,
  onEdit,
  onApplyTariff,
}: {
  shipment: ShipmentDetail
  onEdit: () => void
  onApplyTariff: (rate: number) => void
}) {
  const { t } = useTranslation()
  const [marketRateOpen, setMarketRateOpen] = useState(false)
  const suggestion = useSuggestedPrice({
    shipmentId: shipment.id,
    enabled: marketRateOpen,
  })

  const handleUseRate = (row: MarketRateRow) => {
    onApplyTariff(row.actualPrice ?? row.listedPrice)
  }

  return (
    <SidebarSection
      label={t('payment.title')}
      labelAction={
        <PriceCheckBadge
          label={t('payment.priceCheck')}
          onClick={() => setMarketRateOpen(true)}
        />
      }
    >
      <div className="group/section relative -mx-2.5 rounded-md px-2.5 py-1 hover:bg-gray-50">
        <SectionEditButton label={t('payment.editPayment')} onClick={onEdit} />
        <MoneyGrid shipment={shipment} />
      </div>
      <MarketRateDialog
        open={marketRateOpen}
        onOpenChange={setMarketRateOpen}
        stats={suggestion.stats}
        rows={suggestion.rows}
        isLoading={suggestion.isLoading}
        errorMessage={suggestion.errorMessage}
        onUseRate={handleUseRate}
      />
    </SidebarSection>
  )
}
